import { Check, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface ComparisonFeature {
  feature: string;
  ommni: boolean;
  competitor: boolean;
  link?: string;
}

interface CompetitorComparisonProps {
  title: string;
  competitorName: string;
  competitorUrl: string;
  whyOmmni: string[];
  comparisonTable: ComparisonFeature[];
}

export function CompetitorComparison({
  title,
  competitorName,
  competitorUrl,
  whyOmmni,
  comparisonTable,
}: CompetitorComparisonProps) {
  return (
    <PageLayout>
      <div className="container mx-auto px-4 -mt-16">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-4xl">
            <header className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
              <p className="text-xl text-muted-foreground">
                See how Ommni compares to {competitorName} for modern commerce operations
              </p>
            </header>
            <nav className="flex flex-col sm:flex-row gap-4 justify-center" aria-label="Call to action">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">
                  Start Free Trial <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Talk to Sales</Link>
              </Button>
            </nav>
          </div>
        </section>

        {/* Why Choose Ommni */}
        <section className="py-20 px-4" aria-labelledby="why-ommni-heading">
          <div className="container mx-auto max-w-4xl">
            <header className="mb-12">
              <h2 id="why-ommni-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Why Choose Ommni Over {competitorName}?
              </h2>
              <p className="text-lg text-muted-foreground">
                Built for modern retailers who need speed, flexibility, and scale
              </p>
            </header>
            <div className="grid gap-6">
              {whyOmmni.map((reason, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center" aria-hidden="true">
                        <Check className="h-5 w-5 text-primary" aria-hidden="true" />
                      </div>
                    </div>
                    <div 
                      className="text-foreground leading-relaxed prose-a:text-primary prose-a:underline prose-a:font-medium hover:prose-a:text-primary/80"
                      dangerouslySetInnerHTML={{ __html: reason }}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="py-20 px-4 bg-muted/20" aria-labelledby="comparison-heading">
          <div className="container mx-auto max-w-4xl">
            <header className="text-center mb-12">
              <h2 id="comparison-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Feature Comparison
              </h2>
              <p className="text-lg text-muted-foreground">
                See the differences at a glance
              </p>
            </header>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" role="table" aria-label="Feature comparison between Ommni and competitor">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-4 font-semibold" scope="col">Feature</th>
                      <th className="text-center p-4 font-semibold" scope="col">Ommni</th>
                      <th className="text-center p-4 font-semibold" scope="col">{competitorName}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonTable.map((row, index) => (
                      <tr key={index} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="p-4 font-medium">
                          {row.link ? (
                            <Link to={row.link} className="text-primary hover:underline font-semibold">
                              {row.feature}
                            </Link>
                          ) : (
                            row.feature
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {row.ommni ? (
                            <Check className="h-6 w-6 text-primary mx-auto" aria-label="Supported" />
                          ) : (
                            <X className="h-6 w-6 text-muted-foreground mx-auto" aria-label="Not supported" />
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {row.competitor ? (
                            <Check className="h-6 w-6 text-primary mx-auto" aria-label="Supported" />
                          ) : (
                            <X className="h-6 w-6 text-muted-foreground mx-auto" aria-label="Not supported" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <aside className="mt-6 text-center text-sm text-muted-foreground">
              <p>
                Want to learn more about {competitorName}?{" "}
                <a
                  href={competitorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Visit their website
                </a>
              </p>
            </aside>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4" aria-label="Get started call to action">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to See the Difference?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join leading retailers who've made the switch to Ommni
            </p>
            <nav className="flex flex-col sm:flex-row gap-4 justify-center" aria-label="Footer call to action">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">
                  Start Free Trial <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </nav>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
